import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GitCompare, Clock, TrendingUp, TrendingDown, Minus,
  ChevronRight, AlertTriangle, CheckCircle2, XCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RelatedAudits({ currentAuditId, teamMemberEmail }) {
  const navigate = useNavigate();
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [comparingWith, setComparingWith] = useState(null);

  useEffect(() => {
    if (!currentAuditId) return;
    
    setLoading(true);
    fetch(`${BACKEND}/api/audits/${currentAuditId}/related`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setRelated(data);
        // Auto-load comparison with most recent previous audit
        if (data.length > 0 && data[0].relationship === 'previous') {
          loadComparison(data[0].audit_id);
        }
      })
      .finally(() => setLoading(false));
  }, [currentAuditId]);

  const loadComparison = async (previousAuditId) => {
    setComparingWith(previousAuditId);
    try {
      const res = await fetch(
        `${BACKEND}/api/audits/${currentAuditId}/compare/${previousAuditId}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load comparison');
      setComparison(data);
    } catch (err) {
      console.error('Comparison failed:', err);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (related.length === 0) {
    return null; // No related audits found
  }

  const mostRecentPrevious = related.find(r => r.relationship === 'previous');

  return (
    <Card data-testid="related-audits-card">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <GitCompare className="w-4 h-4 text-primary" />
              Related Audits
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              {related.length} audit{related.length !== 1 ? 's' : ''} from {teamMemberEmail?.split('@')[0] || 'same team member'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Comparison Summary (if available) */}
        {comparison && mostRecentPrevious && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="border-l-4 border-l-primary bg-primary/5 rounded-lg p-3"
          >
            <div className="flex items-start gap-2 mb-2">
              <GitCompare className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">
                  vs Last Audit ({new Date(mostRecentPrevious.created_at).toLocaleDateString()})
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {comparison.summary}
                </p>
              </div>
            </div>

            {/* Statistics Grid */}
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                <div className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="w-3 h-3" />
                  <span className="text-xs font-semibold">Fixed</span>
                </div>
                <p className="text-lg font-bold text-emerald-900 tabular-nums">
                  {comparison.statistics.improved + comparison.statistics.resolved}
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                <div className="flex items-center gap-1 text-amber-700">
                  <Minus className="w-3 h-3" />
                  <span className="text-xs font-semibold">Recurring</span>
                </div>
                <p className="text-lg font-bold text-amber-900 tabular-nums">
                  {comparison.statistics.no_improvement}
                </p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                <div className="flex items-center gap-1 text-red-700">
                  <XCircle className="w-3 h-3" />
                  <span className="text-xs font-semibold">New</span>
                </div>
                <p className="text-lg font-bold text-red-900 tabular-nums">
                  {comparison.statistics.new_issue}
                </p>
              </div>
            </div>

            {/* Resolution Rate */}
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Resolution Rate</span>
              <span className={`font-bold ${
                comparison.statistics.resolution_rate >= 0.7 ? 'text-emerald-700' :
                comparison.statistics.resolution_rate >= 0.4 ? 'text-amber-700' :
                'text-red-700'
              }`}>
                {Math.round(comparison.statistics.resolution_rate * 100)}%
              </span>
            </div>
          </motion.div>
        )}

        {/* Related Audits List */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Audit Timeline
          </p>
          {related.slice(0, 5).map((audit, idx) => (
            <motion.div
              key={audit.audit_id}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => navigate(`/results/${audit.audit_id}`)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent cursor-pointer transition-colors group"
            >
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                audit.relationship === 'previous' ? 'bg-blue-500' : 'bg-purple-500'
              }`} />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  <span className="text-foreground font-medium">
                    {new Date(audit.created_at).toLocaleDateString()}
                  </span>
                  <span className="text-muted-foreground">
                    ({audit.days_gap}d ago)
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="h-4 px-1.5 text-[10px]">
                    {Math.round(audit.sku_overlap * 100)}% overlap
                  </Badge>
                  {audit.recommendation_summary && (
                    <span className="text-[10px] text-muted-foreground">
                      {audit.recommendation_summary.reject} REJECTs
                    </span>
                  )}
                </div>
              </div>

              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </motion.div>
          ))}
        </div>

        {related.length > 5 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            onClick={() => navigate(`/history?team_member=${teamMemberEmail}`)}
          >
            View All {related.length} Audits from this Team Member
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
